import { setCommunityData, setPropertyData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetCommunities, useGetOwnershipGrid, useGetOwnershipSummary } from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

export type OccupancyFilter = "ALL" | "OWNER_OCCUPIED" | "TENANT_OCCUPIED";
export type StatusFilter = "ACTIVE" | "CLOSED" | "ALL";

const usePropertyOwnership = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const communityId = communityData?.communityId;
  const [communityKey, setCommunityKey] = useState(Math.random() * 2);
  const [occupancy, setOccupancy] = useState<OccupancyFilter>("ALL");
  const [status, setStatus] = useState<StatusFilter>("ACTIVE");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // Deep link from Owner Master detail page: ?ownershipId=835&communityId=542
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedCommunityId = searchParams.get("communityId");
  const [ownershipFilter, setOwnershipFilter] = useState<number | null>(() => {
    const id = Number(searchParams.get("ownershipId"));
    return id > 0 ? id : null;
  });
  useEffect(() => {
    if (ownershipFilter) setStatus("ALL"); // a linked ownership may be closed
  }, []);

  const { data: communityList, isLoading: isCommunityLoading } = useGetCommunities({});
  useEffect(() => {
    if (communityList?.data) setCommunityKey(Math.random() * 3);
  }, [communityList]);

  // Switch to the linked ownership's community once the community list is available
  useEffect(() => {
    if (!linkedCommunityId || !communityList?.data || communityId == linkedCommunityId) return;
    const details = communityList.data.find((c: any) => c.communityId == linkedCommunityId);
    if (details) dispatch(setCommunityData(details));
  }, [linkedCommunityId, communityList]);

  const clearOwnershipFilter = () => {
    setOwnershipFilter(null);
    setStatus("ACTIVE");
    setSearchParams({}, { replace: true });
  };

  // Any filter change starts again from page 1 with rows collapsed
  useEffect(() => {
    setPageNumber(0);
    setExpanded({});
  }, [communityId, occupancy, status, search, pageSize, ownershipFilter]);

  const request = useMemo(() => ({
    filters: [
      ...(ownershipFilter ? [{ field: "ownershipId", value: ownershipFilter, operator: "EQUAL" }] : []),
      ...(occupancy === "ALL" ? [] : [{ field: "occupancyType", value: occupancy, operator: "EQUAL" }]),
      ...(status === "ALL" ? [] : [{ field: "status", value: status, operator: "EQUAL" }]),
    ],
    searchFields: ["ownershipCode", "propertyName", "primaryContactName", "primaryPhone", "primaryEmail"],
    search,
    sort: [{ field: "ownershipId", direction: "DESC" }],
    pageNumber,
    pageSize,
  }), [occupancy, status, search, pageNumber, pageSize, ownershipFilter]);

  // Wait for the linked community before querying, so the grid doesn't flash the previous community
  const isSwitchingCommunity = !!linkedCommunityId && !!ownershipFilter && communityId != linkedCommunityId;
  const { data: grid, isLoading: isGridLoading, isFetching: isGridFetching } =
    useGetOwnershipGrid({ communityId, data: request, queryParams: { enabled: !!communityId && communityId != "all" && !isSwitchingCommunity } });

  // Linked ownership: open its people straight away
  useEffect(() => {
    if (ownershipFilter && grid?.data?.length === 1 && grid.data[0].ownershipId == ownershipFilter) {
      setExpanded({ [ownershipFilter]: true });
    }
  }, [grid, ownershipFilter]);
  const { data: summary } = useGetOwnershipSummary({ communityId });

  const onCommunityChange = (value: any) => {
    // A linked PO belongs to its own community; picking another community drops that filter
    if (ownershipFilter) clearOwnershipFilter();
    const details = communityList?.data?.find((c: any) => c.communityId == value);
    dispatch(setCommunityData(details));
  };

  // Sibling Master Data pages: /<admin|cam>/master-data/owner/...
  const ownerBase = location.pathname.replace(/\/[^/]*property-ownership.*$/, `/${routes.owner}`);
  // PO-ID -> ownership (title / customer account) detail page
  const openOwnership = (ownershipId: number) =>
    navigate(`${location.pathname.replace(/\/property-ownership.*$/, "")}/${routes.propertyOwnership}/${ownershipId}`);
  const openOwner = (userId: number) => navigate(`${ownerBase}/${routes.details}/${userId}`);
  // Property Config > Property Management > property page (same community, already selected)
  const openProperty = (propertyId: number, propertyName: string) => {
    dispatch(setPropertyData({ propertyId, propertyName }));
    navigate(`${location.pathname.replace(/\/property-ownership.*$/, "")}/${routes.properties}/${propertyId}/view`);
  };

  return {
    communityList, isCommunityLoading, communityId, communityKey, onCommunityChange,
    occupancy, setOccupancy, status, setStatus,
    searchInput, setSearchInput, applySearch: () => setSearch(searchInput.trim()),
    rows: grid?.data || [], total: grid?.total || 0, isGridLoading, isGridFetching,
    pageNumber, setPageNumber, pageSize, setPageSize,
    expanded, toggleExpanded: (id: number) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] })),
    summary: summary?.data,
    openOwnership, openOwner, openProperty,
    ownershipFilter, clearOwnershipFilter,
  };
};

export default usePropertyOwnership;
